import { useRef, useState } from "react";
import { Upload, Link2, Check } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full bg-[#131327] border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-subtle outline-none focus:border-accent/60 transition-colors";

export function Field({ label, children, hint }) {
  return (
    <div className="mb-4">
      <label className="font-mono-label text-[10px] text-subtle block mb-2">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-subtle mt-1.5">{hint}</p>}
    </div>
  );
}

export function TextInput({ value, onChange, placeholder, testid, type = "text" }) {
  return <input type={type} value={value ?? ""} onChange={onChange} placeholder={placeholder} className={inputCls} data-testid={testid} />;
}

export function TextArea({ value, onChange, rows = 4, placeholder, testid, mono = false }) {
  return (
    <textarea
      value={value ?? ""}
      onChange={onChange}
      rows={rows}
      placeholder={placeholder}
      className={`${inputCls} ${mono ? "font-mono text-xs" : ""} resize-y`}
      data-testid={testid}
    />
  );
}

export function Select({ value, onChange, options, testid }) {
  return (
    <select value={value} onChange={onChange} className={inputCls} data-testid={testid}>
      {options.map((o) => (
        <option key={o.value} value={o.value} className="bg-charcoal">{o.label}</option>
      ))}
    </select>
  );
}

export function Toggle({ checked, onChange, label, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="flex items-center gap-3 group"
      data-testid={testid}
    >
      <span className={`relative h-6 w-11 rounded-full transition-colors duration-300 ${checked ? "bg-accent" : "bg-white/15"}`}>
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all duration-300 ${checked ? "left-[22px]" : "left-0.5"}`} />
      </span>
      {label && <span className="text-sm text-mist group-hover:text-white transition-colors">{label}</span>}
    </button>
  );
}

export function ImageInput({ value, onChange, testid }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/media", fd);
      onChange(data.url);
      toast.success("Media uploaded");
    } catch (e) {
      toast.error("Upload failed — try a file under 10MB");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-start gap-3">
      {value ? (
        <div className="h-16 w-16 rounded-lg overflow-hidden border border-white/10 shrink-0 bg-[#131327]">
          <img src={mediaUrl(value)} alt="preview" className="h-full w-full object-cover" />
        </div>
      ) : (
        <div className="h-16 w-16 rounded-lg border border-dashed border-white/15 shrink-0 flex items-center justify-center text-subtle text-[9px] font-mono-label">NO IMG</div>
      )}
      <div className="flex-1 space-y-2">
        <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="Paste URL or upload" className={inputCls} data-testid={`${testid}-url`} />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg border border-white/15 text-mist hover:text-white hover:border-accent/50 transition-colors"
            data-testid={`${testid}-upload-btn`}
          >
            <Upload className="h-3 w-3" /> {uploading ? "Uploading..." : "Upload"}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => { navigator.clipboard?.writeText(mediaUrl(value)); toast.success("URL copied"); }}
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg border border-white/15 text-mist hover:text-white hover:border-accent/50 transition-colors"
              data-testid={`${testid}-copy-btn`}
            >
              <Link2 className="h-3 w-3" /> Copy URL
            </button>
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/*,video/*" hidden onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testid}-file-input`} />
      </div>
    </div>
  );
}

export function SaveBar({ onSave, saving, testid = "admin-save-btn", label = "Save Changes" }) {
  return (
    <div className="sticky bottom-4 z-20 flex justify-end">
      <button
        onClick={onSave}
        disabled={saving}
        className="inline-flex items-center gap-2 rounded-full bg-accent hover:bg-accent-bright disabled:opacity-60 text-white font-semibold px-7 py-3 text-sm shadow-[0_0_28px_rgba(124,58,237,0.45)] transition-all duration-300 hover:-translate-y-0.5"
        data-testid={testid}
      >
        {saving ? "Saving..." : label}
      </button>
    </div>
  );
}
